const num = (n) => String(n).padStart(2, "0");

export const FieldLabel = ({ n, children, required, help, htmlFor }) => (
  <div className="mb-2">
    <label htmlFor={htmlFor} className="flex items-baseline gap-2.5">
      <span className="text-[#C85A38] text-xs font-medium tabular-nums">{num(n)}</span>
      <span className="text-sm sm:text-[15px] text-[#2E2A26] font-medium leading-snug">
        {children}
        {required && <span className="text-[#C85A38] ml-1" aria-hidden="true">*</span>}
      </span>
    </label>
    {help && <p className="text-xs text-[#2E2A26]/55 mt-1 ml-[1.9rem] leading-relaxed">{help}</p>}
  </div>
);

const inputBase =
  "w-full bg-white border border-stone-200 rounded-xl px-3.5 py-2.5 text-[15px] text-[#2E2A26] placeholder:text-[#2E2A26]/35 focus:outline-none focus:border-[#C85A38] focus:ring-2 focus:ring-[#C85A38]/15 transition-colors duration-150";

const ErrorText = ({ name, error }) =>
  error ? <p className="text-xs text-[#C85A38] mt-1.5" data-testid={`error-${name}`}>{error}</p> : null;

export const TextField = ({ n, label, name, value, onChange, required, help, type = "text", placeholder, error }) => (
  <div data-testid={`field-${name}`}>
    <FieldLabel n={n} required={required} help={help} htmlFor={`f-${name}`}>{label}</FieldLabel>
    <input
      id={`f-${name}`}
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      placeholder={placeholder}
      className={`${inputBase} ${error ? "border-[#C85A38]" : ""}`}
      data-testid={`input-${name}`}
    />
    <ErrorText name={name} error={error} />
  </div>
);

export const TextArea = ({ n, label, name, value, onChange, required, help, rows = 5, error }) => (
  <div data-testid={`field-${name}`}>
    <FieldLabel n={n} required={required} help={help} htmlFor={`f-${name}`}>{label}</FieldLabel>
    <textarea
      id={`f-${name}`}
      name={name}
      rows={rows}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      className={`${inputBase} resize-y leading-relaxed ${error ? "border-[#C85A38]" : ""}`}
      data-testid={`input-${name}`}
    />
    <ErrorText name={name} error={error} />
  </div>
);

export const ChoiceField = ({ n, label, name, value, onChange, options, required, help, error }) => (
  <div data-testid={`field-${name}`}>
    <FieldLabel n={n} required={required} help={help}>{label}</FieldLabel>
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(name, o.value)}
            className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors duration-150 ${
              on ? "bg-[#C85A38] border-[#C85A38] text-white" : "bg-white border-stone-200 text-[#2E2A26]/70 hover:border-stone-300"
            }`}
            data-testid={`choice-${name}-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
    <ErrorText name={name} error={error} />
  </div>
);

export const SelectField = ({ n, label, name, value, onChange, options, required, help }) => (
  <div data-testid={`field-${name}`}>
    <FieldLabel n={n} required={required} help={help} htmlFor={`f-${name}`}>{label}</FieldLabel>
    <select
      id={`f-${name}`}
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      className={`${inputBase} h-11`}
      data-testid={`input-${name}`}
    >
      <option value="">Pick one</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

export const FormSection = ({ title, children }) => (
  <fieldset className="bg-[#F4ECE2] rounded-xl sm:rounded-2xl border border-stone-100 p-5 sm:p-7">
    <legend className="sr-only">{title}</legend>
    <h3 className="font-serif text-2xl text-[#2E2A26] mb-5">{title}</h3>
    <div className="flex flex-col gap-6">{children}</div>
  </fieldset>
);
